import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  eventsPageView,
  exhibitorsPageView,
  postsPageView,
  productsPageView,
} from '@planone/bau365/data-access-fair-data';
import { EntityType } from '@planone/bau365/util-fair-data';
import { catchError, filter, map, Observable, of, switchMap, withLatestFrom } from 'rxjs';

import {
  addToHistory,
  addToHistoryFailure,
  addToHistorySuccess,
  loadUserHistory,
  loadUserHistoryFailure,
  loadUserHistorySuccess,
} from './history.actions';

import { USER_SESSION_STATUS } from '../../config/user-service.config.interface';
import { UserHistoryService } from '../../service/history/history.service';

@Injectable()
export class UserHistoryEffects {
  loadUserHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserHistory),
      switchMap(() =>
        this.service.getUserHistoryEntries().pipe(
          map((entries) => loadUserHistorySuccess({ entries })),
          catchError((error) => of(loadUserHistoryFailure({ error })))
        )
      )
    )
  );

  addToHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addToHistory),
      withLatestFrom(this.userSessionStatus),
      filter(([_, activeSession]) => activeSession),
      switchMap(([{ fairId, entityType, id }]) =>
        this.service.addToHistory(fairId, entityType, id).pipe(
          map((entry) => addToHistorySuccess({ entry })),
          catchError((error) => of(addToHistoryFailure(error)))
        )
      )
    )
  );

  visitExhibitorPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exhibitorsPageView),
      map(({ exhibitor_id, fair_id }) =>
        addToHistory({ fairId: fair_id, entityType: EntityType.exhibitor, id: exhibitor_id })
      )
    )
  );

  visitEventPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(eventsPageView),
      map(({ event_id, fair_id }) => addToHistory({ fairId: fair_id, entityType: EntityType.event, id: event_id }))
    )
  );

  visitPostPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postsPageView),
      map(({ post_id, fair_id }) => addToHistory({ fairId: fair_id, entityType: EntityType.post, id: post_id }))
    )
  );

  visitProductPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productsPageView),
      map(({ productUuid, fairId }) => addToHistory({ fairId, entityType: EntityType.product, id: productUuid }))
    )
  );

  constructor(
    private actions$: Actions,
    private service: UserHistoryService,
    @Inject(USER_SESSION_STATUS) private userSessionStatus: Observable<boolean>
  ) {}
}
